// Helpers for the client-user mode. GET /user returns `client_access` — the
// user's client-account grants, keyed by client_id or shipped as an array.
//
// Roles form a ladder, mirroring isi_data/roles.js (keep the two in step):
//
//   client_admin  the customer's own administrator — their account page and
//                 their people, plus everything below
//   advanced      read + mutations within the account
//   user          read-only within the account
//
// 'viewer' and 'manager' are the pre-2026-08 names for 'user' and 'advanced'.
// The backend normalizes them before they reach the token, but a cached token
// minted before that deploy can still carry them, so they are mapped here too.
//
// NOTE: nothing here is a security control. The server re-resolves the role
// from DynamoDB on every request (isi_data/tenancy.js). These helpers only
// decide what to render, so we never offer a button that would 403.

const ROLE_RANK = {
  user: 1,
  advanced: 2,
  client_admin: 3,
  admin: 99,
};

const LEGACY_ROLE_ALIASES = {
  viewer: "user",
  manager: "advanced",
};

// A stored role string as a current one, or null when unrecognised. Unknown
// values fail closed rather than defaulting to read-only.
export function normalizeRole(storedRole) {
  const role = LEGACY_ROLE_ALIASES[storedRole] || storedRole;
  if (!Object.prototype.hasOwnProperty.call(ROLE_RANK, role)) {
    return null;
  }
  return role;
}

// Does `role` satisfy a check asking for at least `requiredRole`?
export function roleMeets(role, requiredRole) {
  const held = ROLE_RANK[normalizeRole(role)];
  const needed = ROLE_RANK[normalizeRole(requiredRole)];
  if (!held || !needed) {
    return false;
  }
  return held >= needed;
}

export function normalizeClientAccess(userData) {
  const raw = userData?.client_access;
  if (!raw) {
    return [];
  }

  let entries = [];
  if (Array.isArray(raw)) {
    entries = raw;
  } else if (typeof raw === "object") {
    entries = Object.entries(raw).map(([clientId, value]) => {
      if (typeof value === "string") {
        return { client_id: clientId, role: value };
      }
      return { client_id: clientId, ...value };
    });
  }

  return entries
    .map((entry) => ({
      client_id: entry.client_id || "",
      role: normalizeRole(entry.role),
      client_account_uid: entry.client_account_uid || null,
    }))
    .filter((entry) => entry.client_id && entry.role);
}

// A "client user" is logged in without platform-admin rights but with at least
// one client-account grant.
/**
 * The staff user-management pages key grants by client_account_uid and want a
 * plain role string per uid. `client_access` carries a VALUE OBJECT
 * ({role, client_id, client_account_uid}) since the uid-vs-client_id fix; older
 * tokens still hold a bare role string, so both shapes are accepted.
 */
export function grantRoleByUid(rawClientAccess) {
  const byUid = {};
  if (!rawClientAccess || typeof rawClientAccess !== "object") {
    return byUid;
  }
  for (const [uid, value] of Object.entries(rawClientAccess)) {
    const role = typeof value === "string" ? value : value?.role;
    const normalized = normalizeRole(role);
    if (normalized) {
      byUid[uid] = normalized;
    }
  }
  return byUid;
}

export function isClientUser(userData) {
  return !userData?.is_admin && normalizeClientAccess(userData).length > 0;
}

// The caller's role on one client account. Platform admins outrank every grant.
export function roleForClient(userData, clientId) {
  if (userData?.is_admin) {
    return "admin";
  }
  const grant = normalizeClientAccess(userData).find(
    (entry) => entry.client_id === clientId,
  );
  return grant ? grant.role : null;
}

// Does the caller hold at least `requiredRole` on this client account?
export function hasClientRole(userData, clientId, requiredRole) {
  return roleMeets(roleForClient(userData, clientId), requiredRole);
}

// Is the caller a client administrator anywhere? Drives the "My Organization"
// nav entry, which needs to appear before a specific account is in hand.
export function clientAdminGrants(userData) {
  if (userData?.is_admin) {
    return [];
  }
  return normalizeClientAccess(userData).filter((entry) =>
    roleMeets(entry.role, "client_admin"),
  );
}

// The role choices an admin may grant, in ladder order, for a v-select. Passed
// the caller's `t` so this stays framework-free. `cap` limits the list to roles
// at or below the granter's own level — a client admin must not be able to mint
// a role they do not hold themselves.
export function clientRoleOptions(t, cap = "client_admin") {
  return [
    {
      title: t("users.roleUser"),
      subtitle: t("users.roleUserHint"),
      value: "user",
    },
    {
      title: t("users.roleAdvanced"),
      subtitle: t("users.roleAdvancedHint"),
      value: "advanced",
    },
    {
      title: t("users.roleClientAdmin"),
      subtitle: t("users.roleClientAdminHint"),
      value: "client_admin",
    },
  ].filter((option) => roleMeets(cap, option.value));
}

// Human label for a stored role, including pre-rename rows we have not
// migrated yet. Unknown values are shown verbatim so they are visible rather
// than silently rendered as read-only.
export function clientRoleLabel(t, storedRole) {
  const role = normalizeRole(storedRole);
  if (role === "client_admin") {
    return t("users.roleClientAdmin");
  }
  if (role === "advanced") {
    return t("users.roleAdvanced");
  }
  if (role === "user") {
    return t("users.roleUser");
  }
  return storedRole;
}
